import { Done, Clear } from '@mui/icons-material'
import { Button, Alert, CircularProgress, Box, Typography } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { GridColDef, GridColumnHeaderParams, DataGrid } from '@mui/x-data-grid'
import type { TFunction } from 'i18next'
import { JSX, ReactNode } from 'react'

import CheckTooltipDetail from '@/pages/fota/components/CheckToolTipDetails'
import types from '@/pages/Device/DeviceType'
import { Uid } from '@/pages/utils/formatter'

// Common interface for both data types
export interface FotaCheckResult {
  uid: string
  serial: string
  label: string
  checkPassed?: boolean // PreDownloadCheckStep
  check_passed?: boolean // SendToUpdateQueuedStep
  checkDetail?: boolean // PreDownloadCheckStep
  check_detail?: boolean // SendToUpdateQueuedStep
  type?: string // Only in PreDownloadCheckStep
}

interface FotaCheckStepProps {
  isLoading: boolean
  result: FotaCheckResult[]
  error: string | null
  checkPassedCount: number
  checkFailedCount: number
  handleBackClick: () => void
  t: TFunction
  showTypeColumn?: boolean
  customColumns?: GridColDef[]
  dataGridProps?: Record<string, unknown>
  children?: ReactNode
}

export const FotaCheckStep = ({
  isLoading,
  result,
  error,
  checkPassedCount,
  checkFailedCount,
  handleBackClick,
  t,
  showTypeColumn = false,
  customColumns,
  dataGridProps = {},
  children,
}: FotaCheckStepProps): JSX.Element => {
  const renderWrappedHeader = (params: GridColumnHeaderParams) => (
    <div
      style={{
        whiteSpace: 'normal',
        lineHeight: '1.5',
        fontWeight: 600,
        wordBreak: 'break-word',
        padding: '8px 0',
      }}
    >
      {params.colDef.headerName}
    </div>
  )

  const baseColumns: GridColDef[] = [
    {
      field: 'uid',
      headerName: t('di:device.uid'),
      renderHeader: renderWrappedHeader,
      renderCell: (params) => <Uid value={params.value} />,
      width: 400,
      flex: 0,
      minWidth: 400,
      maxWidth: 400,
      resizable: false,
      sortable: true,
    },
    {
      field: 'serial',
      headerName: t('di:device.serialNr'),
      renderHeader: renderWrappedHeader,
      flex: 0.75,
      minWidth: 100,
    },
    {
      field: 'label',
      headerName: t('di:device.ownerLabel'),
      renderHeader: renderWrappedHeader,
      flex: 0.75,
      minWidth: 100,
    },
  ]

  const columnsWithType = showTypeColumn
    ? [
        ...baseColumns,
        {
          field: 'type',
          headerName: t('di:device.type'),
          renderHeader: renderWrappedHeader,
          renderCell: (params: {
            value: string
            row: { deviceRoleName?: string }
          }) => {
            const deviceTypes = types as Record<string, string>
            const deviceType = deviceTypes[params.value] ?? params.value
            const deviceRoleName = params.row.deviceRoleName

            if (deviceRoleName) {
              return (
                <Tooltip title={deviceRoleName} placement="top" arrow>
                  <Box
                    component="span"
                    sx={{ display: 'inline-block', width: '100%' }}
                  >
                    {deviceType}
                  </Box>
                </Tooltip>
              )
            }

            return deviceType
          },
          flex: 0.75,
          minWidth: 100,
        },
      ]
    : baseColumns

  const checkColumn: GridColDef = {
    field: 'checkPassed',
    headerName: t<string>('di:fota.preFotaCheck'),
    renderHeader: renderWrappedHeader,
    flex: 0.75,
    minWidth: 80,
    renderCell: (params) => {
      const isPassed = params.value ?? params.row.check_passed
      const detailData = params.row.checkDetail ?? params.row.check_detail

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            paddingLeft: '16px',
          }}
        >
          <Tooltip
            placement="top"
            title={<CheckTooltipDetail result={detailData} t={t} />}
            arrow
            enterTouchDelay={0}
            leaveTouchDelay={1500}
          >
            <Box component="span" sx={{ display: 'inline-flex' }}>
              {isPassed ? <Done color="primary" /> : <Clear color="error" />}
            </Box>
          </Tooltip>
        </Box>
      )
    },
  }

  const columns = customColumns ?? [...columnsWithType, checkColumn]

  const LoadingComponent = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        flexDirection: 'column',
      }}
    >
      <CircularProgress sx={{ marginTop: 2 }} />
    </Box>
  )

  const DataGridComponent = (
    <Box
      sx={{
        borderRadius: '4px',
        overflow: 'auto',
        width: '100%',
        '& .MuiDataGrid-cell': {
          whiteSpace: 'normal',
          lineHeight: '1.5',
        },
      }}
    >
      <DataGrid
        data-testid="device-grid-fota-check"
        rowHeight={45}
        rows={result}
        columns={columns}
        autoHeight // DO NOT REMOVE - Even though 'autoHeight' is deprecated, the other alternatives do not fix the scrolling issue.
        disableVirtualization
        pageSizeOptions={[5, 10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        getRowId={(row) => row.uid}
        disableColumnFilter={true}
        disableColumnMenu={true}
        disableDensitySelector={true}
        disableColumnSelector={true}
        disableRowSelectionOnClick={true}
        localeText={{ noRowsLabel: 'No records found' }}
        {...dataGridProps}
      />
    </Box>
  )

  const NoResultsComponent = (
    <Box sx={{ padding: 2 }}>
      <Typography variant="body1" color="text.secondary">
        {t('di:fota.noResultsFound')}
      </Typography>
    </Box>
  )

  let contentToRender
  if (isLoading) {
    contentToRender = LoadingComponent
  } else if (result.length === 0) {
    contentToRender = NoResultsComponent
  } else {
    contentToRender = DataGridComponent
  }

  return (
    <>
      {error && (
        <Alert
          data-testid="fota-check-error-alert"
          severity="error"
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}
      {checkPassedCount > 0 || checkFailedCount > 0 ? (
        <Alert data-testid="fota-check-info-alert" severity="info">
          {checkPassedCount > 0 &&
            t('di:fota.fotaCheckPassedMessage', {
              count: checkPassedCount,
            })}
          {checkFailedCount > 0 &&
            t('di:fota.fotaCheckFailedMessage', { count: checkFailedCount })}
        </Alert>
      ) : null}

      {contentToRender}

      {children}

      <Box
        sx={{
          backgroundColor: '#eee',
          paddingTop: '24px',
          borderBottom: 'none !important',
        }}
      >
        <Button
          data-testid="fota-check-back-button"
          variant="contained"
          onClick={handleBackClick}
          sx={{
            marginTop: 2,
            marginRight: 2,
          }}
        >
          {t('common:general.back')}
        </Button>
      </Box>
    </>
  )
}

export default FotaCheckStep
